import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  CircularProgress,
  Alert,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  MoreVert,
  Edit,
  Archive,
  Delete,
  ContentCopy,
  AccessTime,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useServices, useServiceStats, useDeleteService, useToggleServiceStatus, useToggleServiceBooking, useDuplicateService } from '../../hooks/useServices';
import CreateServiceModal from '../../components/services/CreateServiceModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { Service } from '../../services/services';

type StatusFilter = 'all' | 'active' | 'inactive' | 'archived';
type CategoryFilter = string;

const categoryLabels: Record<string, string> = {
  coaching: 'Coaching',
  consulting: 'Consulting',
  strategy: 'Strategy',
  training: 'Training',
  workshop: 'Workshop',
  admin: 'Admin',
  social_media: 'Social Media',
  content: 'Content',
  design: 'Design',
  other: 'Other',
};

const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr`;
  return `${hours}h ${mins}m`;
};

const formatPrice = (service: Service) => {
  if (service.pricingType === 'free') return 'Free';
  if (service.pricingType === 'custom') return 'Custom';

  const amount = service.price / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: service.currency || 'USD',
  }).format(amount);

  return service.pricingType === 'hourly' ? `${formatted}/hr` : formatted;
};

export default function ServicesPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [category, setCategory] = useState<CategoryFilter>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

  // Queries
  const { data, isLoading, error } = useServices({
    page: page + 1,
    limit: rowsPerPage,
    search: search || undefined,
    status,
    category: category || undefined,
    sortBy: 'displayOrder',
    sortOrder: 'asc',
  });

  const { data: stats } = useServiceStats();
  const deleteService = useDeleteService();
  const toggleStatus = useToggleServiceStatus();
  const toggleBooking = useToggleServiceBooking();
  const duplicateService = useDuplicateService();

  const meetingTypeLabels: Record<string, string> = {
    online: 'Online',
    in_person: 'In Person',
    phone: 'Phone',
    flexible: 'Flexible',
  };

  // Handler: Open menu
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, service: Service) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedService(service);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    // Don't clear selectedService here - needed for dialogs
  };

  // Handler: Click on service row
  const handleServiceClick = (service: Service) => {
    setEditingService(service);
    setCreateModalOpen(true);
  };

  // Handler: Edit from menu
  const handleEdit = () => {
    if (selectedService) {
      setEditingService(selectedService);
      setCreateModalOpen(true);
    }
    handleMenuClose();
  };

  // Handler: Duplicate
  const handleDuplicate = async () => {
    if (selectedService) {
      await duplicateService.mutateAsync(selectedService._id);
    }
    handleMenuClose();
    setSelectedService(null);
  };

  // Handler: Open archive dialog
  const handleArchiveClick = () => {
    setArchiveDialogOpen(true);
    handleMenuClose();
  };

  // Handler: Confirm archive
  const handleArchiveConfirm = async () => {
    if (selectedService) {
      await deleteService.mutateAsync({ id: selectedService._id, permanent: false });
    }
    setArchiveDialogOpen(false);
    setSelectedService(null);
  };

  // Handler: Open delete dialog
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Handler: Confirm delete
  const handleDeleteConfirm = async () => {
    if (selectedService) {
      await deleteService.mutateAsync({ id: selectedService._id, permanent: true });
    }
    setDeleteDialogOpen(false);
    setSelectedService(null);
  };

  // Handler: Close modal
  const handleModalClose = () => {
    setCreateModalOpen(false);
    setEditingService(null);
  };

  const handleToggleStatus = async (serviceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await toggleStatus.mutateAsync(serviceId);
  };

  const handleToggleBooking = async (serviceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await toggleBooking.mutateAsync(serviceId);
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stats?.total || 0} total services - {stats?.active || 0} active - {stats?.bookable || 0} bookable
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateModalOpen(true)}
        >
          Add Service
        </Button>
      </Box>

      {/* Stats Cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {[
          { label: 'Active', value: stats?.active || 0, color: '#10B981', filter: 'active' },
          { label: 'Inactive', value: stats?.inactive || 0, color: '#6B7280', filter: 'inactive' },
        ].map((stat) => (
          <Card
            key={stat.label}
            sx={{
              px: 3,
              py: 2,
              minWidth: 120,
              cursor: 'pointer',
              border: status === stat.filter ? `2px solid ${stat.color}` : '2px solid transparent',
              '&:hover': {
                borderColor: stat.color,
              },
            }}
            onClick={() => setStatus(status === stat.filter ? 'all' : stat.filter as StatusFilter)}
          >
            <Typography variant="h5" fontWeight={600}>
              {stat.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {stat.label}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            placeholder="Search services..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load services. Please try again.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Meeting</TableCell>
                  <TableCell align="center">Bookable</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.services.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {search ? 'No services match your search' : 'No services yet. Add your first service!'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.services.map((service) => (
                    <TableRow
                      key={service._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleServiceClick(service)}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: service.color || '#3B82F6',
                            }}
                          />
                          <Box>
                            <Typography fontWeight={500}>
                              {service.name}
                            </Typography>
                            {service.shortDescription && (
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {service.shortDescription}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AccessTime fontSize="small" color="action" />
                          <Typography variant="body2">
                            {service.formattedDuration || formatDuration(service.duration)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {service.formattedPrice || formatPrice(service)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={categoryLabels[service.category] || service.category}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {meetingTypeLabels[service.meetingType] || service.meetingType}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={service.allowClientBooking ? 'Click to hide from booking page' : 'Click to show on booking page'}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleToggleBooking(service._id, e)}
                          >
                            {service.allowClientBooking ? (
                              <Visibility color="primary" />
                            ) : (
                              <VisibilityOff color="disabled" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Toggle active/inactive">
                          <Switch
                            size="small"
                            checked={service.status === 'active'}
                            onClick={(e) => handleToggleStatus(service._id, e)}
                            color="success"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, service)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={data?.pagination.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
          />
        </Card>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ContentCopy fontSize="small" sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        <MenuItem onClick={handleArchiveClick}>
          <Archive fontSize="small" sx={{ mr: 1 }} />
          Archive
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create/Edit Modal */}
      <CreateServiceModal
        open={createModalOpen}
        onClose={handleModalClose}
        service={editingService}
      />

      {/* Archive Confirmation Dialog */}
      <ConfirmDialog
        open={archiveDialogOpen}
        title="Archive Service"
        message={`Are you sure you want to archive "${selectedService?.name}"? It will be hidden from your services list but can be restored later.`}
        confirmText="Archive"
        variant="info"
        onConfirm={handleArchiveConfirm}
        onCancel={() => {
          setArchiveDialogOpen(false);
          setSelectedService(null);
        }}
        loading={deleteService.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Service"
        message={`Are you sure you want to permanently delete "${selectedService?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setSelectedService(null);
        }}
        loading={deleteService.isPending}
      />
    </Box>
  );
}
